import { useCallback, useEffect, useState } from "react";

/**
 * useInstructionsCollapsed — persisted collapse state for the per-application
 * Instructions callout on the review header. Keyed per review (like
 * Table/useColumnWidths) so collapsing one review's instructions doesn't hide
 * another's, and survives reloads via localStorage. Default is expanded.
 */
const storageKey = (reviewId: string) =>
  `accessReview:instructionsCollapsed:${reviewId}`;

const readCollapsed = (reviewId: string): boolean => {
  try {
    return localStorage.getItem(storageKey(reviewId)) === "1";
  } catch {
    return false;
  }
};

export const useInstructionsCollapsed = (reviewId: string) => {
  const [isCollapsed, setIsCollapsed] = useState(() => readCollapsed(reviewId));

  // ReviewDetail (and therefore this hook) is reused across reviewId
  // navigation — the initializer only runs on first mount, so re-load the
  // persisted choice for the new review whenever the id changes, or the
  // previous review's collapse state bleeds through.
  useEffect(() => {
    setIsCollapsed(readCollapsed(reviewId));
  }, [reviewId]);

  const setCollapsed = useCallback(
    (collapsed: boolean) => {
      setIsCollapsed(collapsed);
      try {
        localStorage.setItem(storageKey(reviewId), collapsed ? "1" : "0");
      } catch {
        // storage full / unavailable — keep the in-memory choice anyway
      }
    },
    [reviewId],
  );

  return { isInstructionsCollapsed: isCollapsed, setInstructionsCollapsed: setCollapsed };
};

export default useInstructionsCollapsed;
